/**
 * @file LegalKindPicker.tsx
 * @description Окно «Новый документ» — выбор вида (phase19.16, SB139, SB141;
 * uiux-legal-docs §2, uiux-legal-steps-b-c §6.4): карточки-схемы «Текст»,
 * «PDF-файл», «Группа документов» и четвёртая «Сертификаты производителей»
 * (автоматическая группа, одна на магазин: если уже есть — неактивна с
 * «уже есть — Открыть»). Вид выбирается один раз и потом не меняется.
 *
 * Карточки — данными из реестра `LEGAL_KIND_META`: новый вид = запись.
 * На телефоне окно во весь экран, карточка не ниже 56 px.
 */

import { LEGAL_DOCUMENT_KINDS, type LegalDocumentKind } from "@chaiberi/shared";
import { Link } from "react-router";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { LEGAL_CERTIFICATES_KIND_META, LEGAL_KIND_META, type LegalKindMeta } from "../../lib/legal/legal-doc-view";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (kind: LegalDocumentKind) => void;
  onPickCertificates: () => void;
  /** Адрес карточки уже созданной группы сертификатов; null — группы нет. */
  certificatesHref: string | null;
  /** Идёт создание группы сертификатов. */
  busy?: boolean;
}

const CARD_CLASS =
  "flex min-h-14 w-full items-start gap-3 rounded-xl bg-surface-lowest p-3 text-left outline-none transition-colors [box-shadow:inset_0_0_0_1px_var(--border)] focus-visible:ring-2 focus-visible:ring-ring";

/** Значок, подпись и пояснение карточки. */
function CardBody({ meta, note }: { meta: LegalKindMeta; note?: string }) {
  const Icon = meta.icon;
  return (
    <>
      <Icon aria-hidden className="mt-0.5 size-6 shrink-0 text-primary" />
      <span className="flex min-w-0 flex-col">
        <span className="type-body font-medium text-on-surface">{meta.label}</span>
        <span className="type-caption text-on-surface-variant">{note ?? meta.hint}</span>
      </span>
    </>
  );
}

export function LegalKindPicker({ open, onClose, onPick, onPickCertificates, certificatesHref, busy }: Props) {
  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[30rem]">
        <DialogHeader>
          <DialogTitle>Новый документ</DialogTitle>
          <DialogDescription>Вид выбирается один раз — потом его не поменять.</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-2">
          {LEGAL_DOCUMENT_KINDS.map((kind) => (
            <li key={kind}>
              <button type="button" className={cn(CARD_CLASS, "hover:bg-surface-low")} onClick={() => onPick(kind)} disabled={busy}>
                <CardBody meta={LEGAL_KIND_META[kind]} />
              </button>
            </li>
          ))}
          <li>
            {certificatesHref ? (
              <div aria-disabled="true" className={cn(CARD_CLASS, "opacity-80")}>
                <CardBody meta={LEGAL_CERTIFICATES_KIND_META} note="Уже есть — группа одна на магазин" />
                <Link
                  to={certificatesHref}
                  onClick={onClose}
                  className="type-body ml-auto inline-flex min-h-12 shrink-0 items-center font-medium text-primary hover:underline"
                >
                  Открыть
                </Link>
              </div>
            ) : (
              <button type="button" className={cn(CARD_CLASS, "hover:bg-surface-low")} onClick={onPickCertificates} disabled={busy}>
                <CardBody meta={LEGAL_CERTIFICATES_KIND_META} />
              </button>
            )}
          </li>
        </ul>
      </DialogContent>
    </Dialog>
  );
}
